import type { ComponentPropsWithRef, ReactNode } from 'react';

import { cn } from '../../lib/cn';

export type PanelVariant = 'elevated' | 'bordered' | 'flat';
export type PanelPadding = 'none' | 'sm' | 'md' | 'lg';

/**
 * Four surfaces used to be offered and only two of them could be told apart in
 * dark: `elevated` had a soft near-black shadow (1.26:1 on the charcoal canvas)
 * behind a `--border-subtle` hairline (1.1:1), and `flat` was `--surface-muted`
 * against `--canvas` at 1.06:1 — neither surface had an edge a user could see.
 * Each variant now carries a boundary drawn in `--border`, the token the system
 * reserves for exactly this (3.11:1 on light, 3.66:1 on dark), and `elevated`
 * takes the hard offset shadow the language is built on rather than a soft blur.
 *
 * `glass` is gone rather than restyled. At `bg-surface/70` over `--canvas` its
 * computed fill is within a hair of `--surface`, so in light it was the same
 * panel as `elevated` — and nothing in the library ever composed it over a scrim,
 * which is the only backdrop that would make a blur mean anything. A variant that
 * cannot be distinguished from its neighbour is not a variant.
 */
const VARIANTS: Record<PanelVariant, string> = {
  elevated: 'bg-surface border border-line shadow-hard',
  bordered: 'bg-surface border-2 border-line-strong',
  flat: 'bg-surface-muted border border-line',
};

/**
 * One scale, read whole by the header, the body and the footer — it used to be
 * two, and they disagreed. The header derived its inset from a chain of
 * ternaries and the body from this record, so `md` — the default, and the step
 * every chart frame passes — took no horizontal inset at all while the body
 * under it took `p-6`. Measured on the default: header `padding-left: 0px`,
 * body `24px`.
 *
 * Every region reads the step back whole, including on the side that faces the
 * rule drawn between them, so the rule gets its room from the same scale as the
 * panel's own edge. The alternative — the step outside, a flat 16px against the
 * rule — was measured and rejected: at `md` it leaves the title 24px from the
 * panel's top edge and 16px from the rule beneath it, at `lg` 32px against 16px,
 * and at `none` 12px against 16px, so the rule's room is over-padded relative to
 * the panel edge at the smallest step and under-padded at the largest. A gap
 * that holds one ratio (1.0) across the scale is the point; a flat one holds
 * 1.33/1.0/0.67/0.5, which is the same drift that produced the two copies.
 *
 * A rule also needs room on *both* sides. Zeroing either side — tried, and it
 * shipped — puts the content against the line and the line reads as struck
 * through the text it was meant to separate, which is the reported defect moved
 * off the panel's edge.
 *
 * `none` is 12px rather than `p-0` for the same reason. With the border drawn,
 * `p-0` puts the first character of the body copy on the edge — the user reads
 * that as the panel clipping its own text, not as an edge-to-edge layout. At the
 * body's size, 12px is the smallest step in the scale that reads as a margin.
 */
const PADDING: Record<PanelPadding, string> = {
  none: 'p-3',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

// `title` is omitted from the native props because the DOM attribute is a
// string while this panel's title is arbitrary content.
export interface PanelProps extends Omit<ComponentPropsWithRef<'section'>, 'title'> {
  title?: ReactNode;
  /** Small line under the title. */
  description?: ReactNode;
  /** Rendered at the top-right of the header — buttons, badges, menus. */
  headerActions?: ReactNode;
  footer?: ReactNode;
  variant?: PanelVariant;
  padding?: PanelPadding;
  /** Renders the title as an <h2> by default so pages keep a sane outline. */
  headingLevel?: 2 | 3 | 4;
}

export function Panel({
  title,
  description,
  headerActions,
  footer,
  variant = 'elevated',
  padding = 'md',
  headingLevel = 3,
  className,
  children,
  ...rest
}: PanelProps) {
  const Heading = `h${headingLevel}` as 'h2' | 'h3' | 'h4';
  const hasHeader = Boolean(title || description || headerActions);

  return (
    <section {...rest} className={cn('flex flex-col rounded-xs', VARIANTS[variant], className)}>
      {hasHeader ? (
        <header
          className={cn('flex items-start justify-between gap-4 border-b border-line-subtle', PADDING[padding])}
        >
          <div className="min-w-0">
            {title ? (
              <Heading className="font-display text-lg font-semibold uppercase tracking-title text-heading">
                {title}
              </Heading>
            ) : null}
            {description ? <p className="mt-1 text-sm text-muted">{description}</p> : null}
          </div>
          {headerActions ? <div className="flex shrink-0 items-center gap-2">{headerActions}</div> : null}
        </header>
      ) : null}

      {/* The measure applies to prose, not to the slot: `[&_p]` caps a paragraph
          without touching a chart, a table or a grid a caller puts in the same
          container. Measured on /components, a Panel body paragraph ran to 146
          characters on one line at 13px — over twice the readable maximum, and
          the eye loses its place on the return sweep. `65ch` is Inter's "0"
          advance (7.57px at 12px, 8.2px at 13px), so it lands at ~75 characters
          of running text, which is the top of the readable range. */}
      <div className={cn('min-w-0 flex-1 [&_p]:max-w-[65ch]', PADDING[padding])}>{children}</div>

      {footer ? <footer className={cn('border-t border-line-subtle', PADDING[padding])}>{footer}</footer> : null}
    </section>
  );
}
